import type { AccountMovement } from '../types/generated';
import { formatCurrency } from './billing';

/**
 * Format an account-movement amount with enough fractional precision to
 * surface sub-cent charges (e.g. token-based PAYG chat costs). The default
 * two-decimal formatter renders €0.00325 as "€0.00", which hides real cost
 * from users. Six digits is enough for any plausible per-request charge while
 * keeping deposits like "€25.00" clean (trailing zeros are trimmed by Intl).
 */
export function formatAccountMovementAmount(row: AccountMovement): string {
  return formatCurrency([row.currency, Math.abs(row.amount)], { maxFractionDigits: 6 });
}

/**
 * Reformat a `tokens:...` features string into a human-readable label.
 *
 * The backend stores PAYG chat charges as:
 *   `tokens:request=<id>,tier=Standard,input=4093,output=139`
 *   `tokens:request=<id>,model=openai/gpt-4o-mini,input=...,...`
 *
 * Other movement types (deposits, subscription charges) use plain strings —
 * those are passed through verbatim. Returns `''` for null/empty input so the
 * caller can choose its own fallback (e.g. `'-'`).
 */
export function formatAccountMovementFeatures(features?: string | null): string {
  if (!features) return '';
  if (!features.startsWith('tokens:')) return features;

  const parsed: Record<string, string> = {};
  for (const part of features.substring('tokens:'.length).split(',')) {
    const eq = part.indexOf('=');
    if (eq <= 0) continue;
    parsed[part.substring(0, eq)] = part.substring(eq + 1);
  }

  // Prefer the user-facing tier label over the raw provider model.
  const bucket = parsed.tier
    ? `Tier: ${parsed.tier}`
    : parsed.model
      ? `Model: ${parsed.model}`
      : null;

  if (bucket && parsed.input && parsed.output) {
    return `${bucket}, Input: ${parsed.input}, Output: ${parsed.output}`;
  }
  return features;
}
